import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_TYPES = [
  { value: "price-list", label: "req_price_list" },
  { value: "brochure", label: "req_brochure" },
  { value: "availability", label: "req_availability" },
  { value: "viewing", label: "req_viewing" },
  { value: "consultation", label: "req_consultation" },
];

export default function ContactForm({
  defaultDevelopmentSlug = "",
  sourcePage = "",
  compact = false,
  testIdPrefix = "contact-form",
}) {
  const location = useLocation();
  const { t, lang } = useI18n();
  const params = new URLSearchParams(location.search);
  const initialRequest = params.get("request") || "price-list";
  // When the form is placed on a specific development page, that dev is fixed;
  // otherwise the visitor picks from the dropdown.
  const isFixedToDev = Boolean(defaultDevelopmentSlug);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    interest: defaultDevelopmentSlug,
    message: "",
    request_type: initialRequest,
  });
  const [devOptions, setDevOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (isFixedToDev) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/developments`);
        setDevOptions(
          (data || []).map((d) => ({ slug: d.slug, name: d.name }))
        );
      } catch (_err) {
        /* fallback: leave dropdown empty */
      }
    })();
  }, [isFixedToDev]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t("form_err_required"));
      return;
    }
    setSubmitting(true);
    try {
      const chosenSlug = defaultDevelopmentSlug || form.interest || null;
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        country: form.country || null,
        interest: chosenSlug,
        message: form.message || null,
        development_slug: chosenSlug,
        source_page: sourcePage || null,
        request_type: form.request_type,
        source: "landing-page",
        language: lang,
        page_path: location.pathname,
      });
      setDone(true);
      toast.success(t("form_success_toast"));
    } catch (err) {
      console.error(err);
      toast.error(t("form_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#1A1A1A]/20 py-3.5 px-0 text-[#1A1A1A] placeholder:text-[#73706B]/60 focus:outline-none focus:border-[#C5A880] rounded-none transition-colors";
  const labelCls =
    "text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block";

  if (done) {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className="border border-[#E5DFD5] p-10 lg:p-14 text-center bg-[#FFFFFF]"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 border border-[#C5A880] mb-6">
          <Check className="w-6 h-6 text-[#C5A880]" strokeWidth={1.25} />
        </div>
        <h3
          className="text-3xl lg:text-4xl font-light text-[#1A1A1A] mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {t("form_thanks", { name: form.name.split(" ")[0] })}
        </h3>
        <p className="text-sm text-[#73706B] max-w-md mx-auto">
          {t("form_thanks_body")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`${testIdPrefix}-root`}
      className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-x-10 gap-y-8`}
    >
      <div>
        <label className={labelCls}>{t("form_full_name")}</label>
        <input
          required
          data-testid={`${testIdPrefix}-name`}
          value={form.name}
          onChange={update("name")}
          className={inputCls}
          placeholder={t("form_name_ph")}
        />
      </div>
      <div>
        <label className={labelCls}>{t("form_email")}</label>
        <input
          required
          type="email"
          data-testid={`${testIdPrefix}-email`}
          value={form.email}
          onChange={update("email")}
          className={inputCls}
          placeholder="you@example.com"
        />
      </div>
      <div>
        <label className={labelCls}>{t("form_phone")}</label>
        <input
          data-testid={`${testIdPrefix}-phone`}
          value={form.phone}
          onChange={update("phone")}
          className={inputCls}
          placeholder="+44 7700 000 000"
        />
      </div>
      <div>
        <label className={labelCls}>{t("form_country")}</label>
        <input
          data-testid={`${testIdPrefix}-country`}
          value={form.country}
          onChange={update("country")}
          className={inputCls}
          placeholder={t("form_country_ph")}
        />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className={labelCls}>{t("form_request_label")}</label>
        <select
          data-testid={`${testIdPrefix}-request-type`}
          value={form.request_type}
          onChange={update("request_type")}
          className={`${inputCls} cursor-pointer`}
        >
          {REQUEST_TYPES.map((r) => (
            <option key={r.value} value={r.value}>
              {t(r.label)}
            </option>
          ))}
        </select>
      </div>
      {!isFixedToDev && (
        <div className={compact ? "" : "md:col-span-2"}>
          <label className={labelCls}>{t("form_dev_label")}</label>
          <select
            data-testid={`${testIdPrefix}-development`}
            value={form.interest}
            onChange={update("interest")}
            className={`${inputCls} cursor-pointer`}
          >
            <option value="">{t("form_dev_any")}</option>
            {devOptions.map((d) => (
              <option key={d.slug} value={d.slug}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className={compact ? "" : "md:col-span-2"}>
        <label className={labelCls}>{t("form_message")}</label>
        <textarea
          data-testid={`${testIdPrefix}-message`}
          value={form.message}
          onChange={update("message")}
          rows={3}
          className={`${inputCls} resize-none`}
          placeholder={t("form_message_ph")}
        />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <button
          type="submit"
          disabled={submitting}
          data-testid={`${testIdPrefix}-submit`}
          className="bg-[#1A1A1A] text-[#F9F6F0] px-10 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] transition-colors duration-300 disabled:opacity-60 inline-flex items-center gap-3"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
          {submitting ? t("form_sending") : t("form_submit")}
        </button>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#73706B] mt-5">
          {t("form_confidential")}
        </p>
      </div>
    </form>
  );
}
